// Core
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

// Library
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

// Components
import StepProgress from 'components/Onboarding/StepProgress/StepProgress'
import BlockQuote from 'components/Onboarding/BlockQuote/BlockQuote'
import BulletList from 'components/Onboarding/BulletList/BulletList'
import IntegrationsList from 'components/Onboarding/IntegrationsList/IntegrationsList'
import CardDisclaimer from 'components/Onboarding/Disclaimer/CardDisclaimer'
import SignupDisclaimer from 'components/Onboarding/Disclaimer/SignupDisclaimer'
import Button from 'components/Onboarding/Button/Button'
import SignupSuccessModal from 'components/Onboarding/SignupSuccessModal/SignupSuccessModal'

// Form Components
import TextInput from 'components/Onboarding/FormFields/TextInput'
import PasswordInput from 'components/Onboarding/FormFields/PasswordInput'

// Actions
import { signInPostSignup, signOutSimple, signup } from 'state-manager/actions/auth'

// Helpers
import { hasError } from 'helpers/field-has-errors'
import { getNameValidation, getPasswordValidation } from 'utils/validation'

// Constants
import { formFields } from 'pages/Onboarding/Signup/constants'
import { EMAIL_REGEX } from 'constants/regex'

// Assets
import { ReactComponent as Logo } from 'assets/icons/logo_clicktech.svg'
import { ReactComponent as IconDD } from 'assets/icons/logo_dd.svg'

// Styles
import classes from 'pages/Onboarding/Onboarding.module.scss'

// Routes
import routes from 'routes'

// Store
import { useAppDispatch } from 'state-manager/store'

// Utils
import {sendGTMEvent} from 'utils/gtm/sendGTMEvent'
import notification from 'utils/notifications'

const Signup = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const dispatch = useAppDispatch()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },

    validationSchema: Yup.object({
      firstName: getNameValidation('First name'),
      lastName: getNameValidation('Last name'),
      email: Yup
        .string()
        .label('Email')
        .matches(EMAIL_REGEX, 'Enter a valid email address')
        .required(),
      password: getPasswordValidation('Password'),
    }),

    onSubmit: async (values) => {
      const { 
        firstName, 
        lastName, 
        email, 
        password, 
      } = values
      const formData = {
        firstName,
        lastName,
        email,
        password,
        terms: true,
      }

      if (executeRecaptcha) {
        const recaptchaToken = await executeRecaptcha('signup')
        const { status } = await dispatch(signup({ ...formData, recaptchaToken }))
        if (status === 200) {
          sendGTMEvent({
            event: 'signup'
          })

          const postSignupRecaptchaToken= await executeRecaptcha('signup')

          await dispatch(signInPostSignup(
            {
              email,
              password,
              recaptchaToken: postSignupRecaptchaToken
            },
          ))
          setShowSuccessModal(true)
        }
      } else {
        notification.error('Something went wrong. Try again!')
      }
    },
  })

  const fistNameHasError = hasError('firstName', formik.touched, formik.errors)
  const lastNameHasError = hasError('lastName', formik.touched, formik.errors)
  const emailHasError = hasError('email', formik.touched, formik.errors)
  const passwordHasError = hasError('password', formik.touched, formik.errors)
  const isSubmitDisabled = !formik.isValid || !formik.dirty

  useEffect(() => {
    dispatch(signOutSimple())
    localStorage.removeItem('onboarding')
  }, [dispatch])

  return (
    <div className={clsx(classes.stepContainer)}>
      <div className={clsx(classes.stepAction)}>
        <div className={clsx(classes.stepSection, 'mt-2', 'mt-xl-up-5')}>
          <div className={clsx(classes.logo)}>
            <Logo />
          </div>
        </div>
        <div className={clsx(classes.stepSection)}>
          <div className={clsx(classes.stepProgress)}>
            <StepProgress step={1} label="account" theme="active" />
            <StepProgress step={2} label="profile" theme="pending" />
            <StepProgress step={3} label="usage" theme="pending" className="d-none-sm" />
            <StepProgress step={4} label="connect" theme="pending" className="d-none-sm" />
          </div>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2')}>          
          <h1>Create Your Free EDEE Account</h1>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className={clsx(classes.stepSection, 'mt-sm-0 mt-2', 'mt-xl-up-4', 'mb-4')}>          
            <div className="form-group mt-0">
              <TextInput
                label="First Name"
                suffix="Required"
                name={formFields.firstName}
                value={formik.values.firstName}
                errorMsg={fistNameHasError && formik.errors.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Your first name"
                dataCy="firstName"
              />
            </div>
            <div className="form-group mt-sm-0 mt-2 mt-xl-up-4">
              <TextInput
                label="Last Name"
                suffix="Required"
                name={formFields.lastName}
                value={formik.values.lastName}
                errorMsg={lastNameHasError && formik.errors.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Your last name"
                dataCy="lastName"
              />
            </div>
            <div className="form-group mt-sm-0 mt-2 mt-xl-up-4">
              <TextInput
                label="Email"
                suffix="Required"
                name={formFields.email}
                value={formik.values.email}
                errorMsg={emailHasError && formik.errors.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Work email"
                dataCy="email"
              />
            </div>
            <div className="form-group mt-sm-0 mt-2 mt-xl-up-4">
              <PasswordInput
                label="Password"
                suffix="Required"
                name={formFields.password}
                value={formik.values.password}
                errorMsg={passwordHasError && formik.errors.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Create a secure password"
                dataCy="email"
              />
            </div>
          </div>
          <div className={clsx(classes.stepSection)}>          
            <CardDisclaimer />
          </div>
          <div className={clsx(classes.stepSection, 'mt-sm-0 mt-2 mt-xl-up-4', 'mb-4')}>
            <div className={clsx(classes.existingAccount)}>
              <div className="d-flex">
                <span className="mr-1">Already have an account?</span>
                <Link to={routes.modalSignIn[0]} data-cy="login">
                  Login
                </Link>
              </div>
              <Button 
                type="submit"
                disabled={isSubmitDisabled}
                title="Create Free Account"
                dataCy="submit"
              />
            </div>       
          </div>
        </form>
        <div className={clsx(classes.stepSection, 'mt-sm-0 mt-2', 'mb-4')}>          
          <SignupDisclaimer />
        </div>
      </div>
      <div className={clsx(classes.infoContainer)}>
        <div className={clsx(classes.infoSection)}>
          <h2 className="fw-semibold mt-sm-2 mt-0 mt-xl-up-2">
            Optimize your PPC budget management budget with ease
          </h2>
        </div>
        <div className={clsx(classes.infoSection, 'mt-sm-2 mt-0 mt-xl-up-2')}>
          <BulletList
            features={[
              'Automated budget management software with OptiPacer',
              'Prevent campaign overspend with alerts & automations',
              'Track performance vs. KPIs',
              'Manage multiple platforms in one place',
              'Save hours on manual budget management',
            ]} 
          />
        </div>
        <div className={clsx(classes.infoSection, 'mt-sm-2 mt-0 mt-xl-up-2')}>
          <h3 className="fw-semibold mt-sm-4 mt-2 mt-xl-up-4">
            Integrates with:
          </h3>
          <IntegrationsList />
        </div>
        <div className={clsx(classes.infoSection, 'mt-sm-8 mt-4 mt-xl-up-8')}>
          <BlockQuote
            srcImage={<IconDD />}
            quote="EDEE increased our efficiency, saved time and improved the service we deliver to our clients."
            author="Andrew Hill, MD at Distribute Digital"
          />
        </div>
        <div className={clsx(classes.infoSection, 'mt-sm-2 mt-0 mt-xl-up-2')}>
          <h4 className="fw-semibold mb-4">
            50% reduction in time spent managing budget pacing for Distribute Digital
          </h4>
        </div>
      </div>
      {showSuccessModal
        ? <SignupSuccessModal />
        : null}
    </div>
  )
}

export default Signup
